import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Users, Search, MessageSquare, Phone, Video, Calendar, CheckSquare, 
    Clock, Flame, Star, MapPin, Mail, Building, ChevronRight,
    Circle, Filter, MoreHorizontal, UserPlus, Settings, Briefcase,
    Target, TrendingUp, Award, Zap, Eye
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Status color mapping
const STATUS_COLORS = {
    online: '#34c759',
    away: '#ffcc00',
    busy: '#ff9f0a',
    dnd: '#ff3b30',
    offline: '#8e8e93'
};

const STATUS_LABELS = {
    online: 'Online',
    away: 'Abwesend',
    busy: 'Beschäftigt',
    dnd: 'Nicht stören',
    offline: 'Offline'
};

const TeamPage = () => {
    const { user, api, updateUser } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [teamMembers, setTeamMembers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [roleFilter, setRoleFilter] = useState('all');
    const [selectedMember, setSelectedMember] = useState(null);
    const [memberTasks, setMemberTasks] = useState([]);
    const [memberStats, setMemberStats] = useState(null);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [teamStats, setTeamStats] = useState({
        totalMembers: 0,
        onlineCount: 0,
        openTasks: 0,
        completedToday: 0
    });

    // Fetch team data
    useEffect(() => {
        fetchTeamData();
        const interval = setInterval(fetchTeamData, 30000); // Refresh every 30s
        return () => clearInterval(interval);
    }, []);

    const fetchTeamData = async () => {
        try {
            const [usersRes, tasksRes] = await Promise.all([
                api.get('/users'),
                api.get('/tasks')
            ]);

            const users = usersRes.data.filter(u => u.is_active !== false);
            const tasks = tasksRes.data;

            // Calculate stats for each user
            const usersWithStats = users.map(u => {
                const userTasks = tasks.filter(t => t.assigned_to?.includes(u.id));
                const openTasks = userTasks.filter(t => t.status !== 'done').length;
                const completedTasks = userTasks.filter(t => t.status === 'done').length;
                
                return {
                    ...u,
                    openTasks,
                    completedTasks,
                    totalTasks: userTasks.length
                };
            });

            setTeamMembers(usersWithStats);

            // Calculate team stats
            const onlineCount = users.filter(u => u.status === 'online').length;
            const allOpenTasks = tasks.filter(t => t.status !== 'done').length;
            const today = new Date().toISOString().split('T')[0];
            const completedToday = tasks.filter(t => 
                t.status === 'done' && t.updated_at?.startsWith(today)
            ).length;

            setTeamStats({
                totalMembers: users.length,
                onlineCount,
                openTasks: allOpenTasks,
                completedToday
            });

            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch team data:', error);
            toast.error('Fehler beim Laden der Team-Daten');
            setLoading(false);
        }
    };

    const fetchMemberDetails = async (member) => {
        try {
            const [tasksRes, workloadRes] = await Promise.all([
                api.get('/tasks', { params: { assigned_to: member.id, view_all: true } }),
                api.get(`/users/${member.id}/workload`)
            ]);

            setMemberTasks(tasksRes.data);

            // Use the calculated workload from backend
            const workload = workloadRes.data;
            setMemberStats({
                openTasks: workload.open_tasks_count,
                overdueTasks: workload.overdue_tasks_count || 0,
                overdueHours: workload.overdue_estimated_hours || 0,
                completedTasks: tasksRes.data.filter(t => t.status === 'done').length,
                workloadPercent: Math.round(workload.workload_percent),
                estimatedHours: workload.total_estimated_hours,
                weeklyCapacity: workload.weekly_capacity_hours,
                status: workload.status,
                statusLabel: workload.status_label,
                recommendation: workload.recommendation,
                taskEstimates: workload.task_estimates || [],
                categoryStats: workload.category_stats || {}
            });
        } catch (error) {
            console.error('Failed to fetch member details:', error);
            // Fallback to simple calculation
            const tasksRes = await api.get('/tasks', { params: { assigned_to: member.id, view_all: true } });
            setMemberTasks(tasksRes.data);
            const openTasks = tasksRes.data.filter(t => t.status !== 'done');
            setMemberStats({
                openTasks: openTasks.length,
                completedTasks: tasksRes.data.filter(t => t.status === 'done').length,
                workloadPercent: Math.min(openTasks.length * 5, 150),
                estimatedHours: openTasks.length * 2,
                weeklyCapacity: member.weekly_hours || 40
            });
        }
    };

    const handleMemberClick = (member) => {
        setSelectedMember(member);
        setShowProfileModal(true);
        fetchMemberDetails(member);
    };

    const handleStartChat = (memberId) => {
        navigate(`/chat?user=${memberId}`);
    };

    const handleStartCall = (memberId) => {
        // Navigate to chat with call intent
        navigate(`/chat?user=${memberId}&call=true`);
    };

    const handleAssignTask = (memberId) => {
        navigate(`/tasks?assign=${memberId}`);
    };

    const updateMyStatus = async (newStatus) => {
        try {
            await api.put(`/users/${user.id}`, { status: newStatus });
            // Update local user state immediately (no reload needed)
            updateUser({ status: newStatus });
            toast.success(`Status auf "${STATUS_LABELS[newStatus]}" geändert`);
            fetchTeamData();
        } catch (error) {
            toast.error('Fehler beim Ändern des Status');
        }
    };

    // Filter members
    const filteredMembers = useMemo(() => {
        return teamMembers.filter(member => {
            const matchesSearch = 
                member.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                member.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                member.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                member.role?.toLowerCase().includes(searchQuery.toLowerCase());
            
            const matchesStatus = statusFilter === 'all' || member.status === statusFilter;
            const matchesRole = roleFilter === 'all' || member.role === roleFilter;
            
            return matchesSearch && matchesStatus && matchesRole;
        });
    }, [teamMembers, searchQuery, statusFilter, roleFilter]);

    // Get unique roles for filter
    const uniqueRoles = useMemo(() => {
        const roles = [...new Set(teamMembers.map(m => m.role).filter(Boolean))];
        return roles;
    }, [teamMembers]);

    const getInitials = (member) => {
        return `${member.first_name?.[0] || ''}${member.last_name?.[0] || ''}`.toUpperCase();
    };

    const getWorkloadColor = (percent) => {
        if (percent >= 100) return '#ff3b30';
        if (percent >= 80) return '#ff9f0a';
        return '#34c759';
    };

    if (loading) {
        return (
            <div className="p-6 md:p-8 flex items-center justify-center min-h-[50vh]">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="team-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        <Users className="w-8 h-8 text-primary" />
                        Team Hub
                    </h1>
                    <p className="text-muted-foreground">Dein Team auf einen Blick</p>
                </div>

                {/* My Status Selector */}
                <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">Mein Status:</span>
                    <Select value={user.status || 'online'} onValueChange={updateMyStatus}>
                        <SelectTrigger className="w-[140px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="online">
                                <div className="flex items-center gap-2">
                                    <Circle className="w-3 h-3 fill-green-500 text-green-500" />
                                    Online
                                </div>
                            </SelectItem>
                            <SelectItem value="busy">
                                <div className="flex items-center gap-2">
                                    <Circle className="w-3 h-3 fill-yellow-500 text-yellow-500" />
                                    Beschäftigt
                                </div>
                            </SelectItem>
                            <SelectItem value="dnd">
                                <div className="flex items-center gap-2">
                                    <Circle className="w-3 h-3 fill-red-500 text-red-500" />
                                    Nicht stören
                                </div>
                            </SelectItem>
                            <SelectItem value="offline">
                                <div className="flex items-center gap-2">
                                    <Circle className="w-3 h-3 fill-gray-400 text-gray-400" />
                                    Offline
                                </div>
                            </SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <Card className="shadow-soft">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-primary/10">
                                <Users className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{teamStats.totalMembers}</p>
                                <p className="text-xs text-muted-foreground">Team Mitglieder</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="shadow-soft">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-green-500/10">
                                <Circle className="w-5 h-5 text-green-500 fill-green-500" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{teamStats.onlineCount}</p>
                                <p className="text-xs text-muted-foreground">Gerade online</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="shadow-soft">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-orange-500/10">
                                <CheckSquare className="w-5 h-5 text-orange-500" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{teamStats.openTasks}</p>
                                <p className="text-xs text-muted-foreground">Offene Tasks</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="shadow-soft">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-emerald-500/10">
                                <Zap className="w-5 h-5 text-emerald-500" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold">{teamStats.completedToday}</p>
                                <p className="text-xs text-muted-foreground">Heute erledigt</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <div className="space-y-6">
                {/* Main Team List */}
                <div>
                    {/* Search & Filters */}
                    <div className="flex flex-col md:flex-row gap-3 mb-6">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Teammitglied suchen..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9"
                                data-testid="team-search"
                            />
                        </div>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-[140px]">
                                <SelectValue placeholder="Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Alle Status</SelectItem>
                                <SelectItem value="online">Online</SelectItem>
                                <SelectItem value="busy">Beschäftigt</SelectItem>
                                <SelectItem value="dnd">Nicht stören</SelectItem>
                                <SelectItem value="offline">Offline</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select value={roleFilter} onValueChange={setRoleFilter}>
                            <SelectTrigger className="w-[160px]">
                                <SelectValue placeholder="Rolle" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Alle Rollen</SelectItem>
                                {uniqueRoles.map(role => (
                                    <SelectItem key={role} value={role} className="capitalize">{role}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Team Members Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <AnimatePresence>
                            {filteredMembers.map((member, index) => (
                                <motion.div
                                    key={member.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ delay: index * 0.05 }}
                                >
                                    <Card 
                                        className="shadow-soft hover:shadow-md transition-all cursor-pointer group relative overflow-hidden"
                                        onClick={() => handleMemberClick(member)}
                                        data-testid={`team-member-${member.id}`}
                                    >
                                        <CardContent className="p-4">
                                            <div className="flex items-start gap-4">
                                                {/* Avatar with Status */}
                                                <div className="relative">
                                                    <Avatar className="w-14 h-14 border-2 border-background shadow">
                                                        {member.profile_image ? (
                                                            <AvatarImage src={member.profile_image} />
                                                        ) : null}
                                                        <AvatarFallback 
                                                            style={{ backgroundColor: member.profile_color || '#009dde' }}
                                                            className="text-white font-semibold"
                                                        >
                                                            {getInitials(member)}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div 
                                                        className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-background"
                                                        style={{ backgroundColor: STATUS_COLORS[member.status || 'offline'] }}
                                                        title={STATUS_LABELS[member.status || 'offline']}
                                                    />
                                                </div>

                                                {/* Info */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <h3 className="font-semibold truncate">
                                                            {member.first_name} {member.last_name}
                                                        </h3>
                                                        {member.id === user.id && (
                                                            <Badge variant="outline" className="text-xs">Du</Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-sm text-muted-foreground capitalize">{member.display_title || member.role}</p>
                                                    
                                                    {/* Contact Info */}
                                                    <div className="flex flex-col gap-0.5 mt-1.5 text-xs text-muted-foreground">
                                                        {member.email && (
                                                            <span className="flex items-center gap-1 truncate">
                                                                <Mail className="w-3 h-3 flex-shrink-0" />
                                                                {member.email}
                                                            </span>
                                                        )}
                                                        {member.phone && (
                                                            <span className="flex items-center gap-1">
                                                                <Phone className="w-3 h-3 flex-shrink-0" />
                                                                {member.phone}
                                                            </span>
                                                        )}
                                                    </div>

                                                    {/* Skills Preview */}
                                                    {member.skills && member.skills.length > 0 && (
                                                        <div className="flex items-center gap-1 mt-2 flex-wrap">
                                                            {member.skills.slice(0, 2).map((skill, idx) => (
                                                                <Badge key={idx} variant="secondary" className="text-xs px-1.5 py-0">
                                                                    {skill}
                                                                </Badge>
                                                            ))}
                                                            {member.skills.length > 2 && (
                                                                <Badge variant="outline" className="text-xs px-1.5 py-0">
                                                                    +{member.skills.length - 2}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    )}
                                                    
                                                    {/* Task Stats */}
                                                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                                        <span className="flex items-center gap-1">
                                                            <CheckSquare className="w-3 h-3" />
                                                            {member.openTasks} offen
                                                        </span>
                                                    </div>
                                                </div>

                                                <ChevronRight className="w-5 h-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </div>

                                            {/* Quick Actions - Show on Hover */}
                                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background via-background to-transparent pt-8 pb-2 px-4 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                                <div className="flex items-center justify-center gap-2 pointer-events-auto">
                                                    <Button 
                                                        size="sm" 
                                                        variant="ghost"
                                                        onClick={(e) => { e.stopPropagation(); handleStartChat(member.id); }}
                                                        className="h-8 w-8 p-0"
                                                        title="Chat starten"
                                                    >
                                                        <MessageSquare className="w-4 h-4" />
                                                    </Button>
                                                    <Button 
                                                        size="sm" 
                                                        variant="ghost"
                                                        onClick={(e) => { e.stopPropagation(); handleStartCall(member.id); }}
                                                        className="h-8 w-8 p-0"
                                                        title="Anrufen"
                                                    >
                                                        <Phone className="w-4 h-4" />
                                                    </Button>
                                                    <Button 
                                                        size="sm" 
                                                        variant="ghost"
                                                        onClick={(e) => { e.stopPropagation(); handleAssignTask(member.id); }}
                                                        className="h-8 w-8 p-0"
                                                        title="Task zuweisen"
                                                    >
                                                        <CheckSquare className="w-4 h-4" />
                                                    </Button>
                                                    <Button 
                                                        size="sm" 
                                                        variant="ghost"
                                                        onClick={(e) => { e.stopPropagation(); navigate(`/calendar?invite=${member.id}`); }}
                                                        className="h-8 w-8 p-0"
                                                        title="Meeting planen"
                                                    >
                                                        <Calendar className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>

                    {filteredMembers.length === 0 && (
                        <div className="text-center py-12 text-muted-foreground">
                            <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>Keine Teammitglieder gefunden</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Member Profile Modal */}
            <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    {selectedMember && (
                        <>
                            <DialogHeader>
                                <div className="flex items-start gap-4">
                                    <div className="relative">
                                        <Avatar className="w-20 h-20 border-4 border-background shadow-lg">
                                            {selectedMember.profile_image ? (
                                                <AvatarImage src={selectedMember.profile_image} />
                                            ) : null}
                                            <AvatarFallback 
                                                style={{ backgroundColor: selectedMember.profile_color || '#009dde' }}
                                                className="text-white text-2xl font-semibold"
                                            >
                                                {getInitials(selectedMember)}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div 
                                            className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full border-3 border-background"
                                            style={{ backgroundColor: STATUS_COLORS[selectedMember.status || 'offline'] }}
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <DialogTitle className="text-xl">
                                            {selectedMember.first_name} {selectedMember.last_name}
                                        </DialogTitle>
                                        <p className="text-muted-foreground capitalize">{selectedMember.display_title || selectedMember.role}</p>
                                        <div className="flex items-center gap-2 mt-2">
                                            <Badge 
                                                style={{ 
                                                    backgroundColor: STATUS_COLORS[selectedMember.status || 'offline'],
                                                    color: 'white'
                                                }}
                                            >
                                                {STATUS_LABELS[selectedMember.status || 'offline']}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>
                            </DialogHeader>

                            <Tabs defaultValue="info" className="mt-4">
                                <TabsList className="grid grid-cols-3 w-full">
                                    <TabsTrigger value="info">Profil</TabsTrigger>
                                    <TabsTrigger value="tasks">Tasks</TabsTrigger>
                                    <TabsTrigger value="workload">Workload</TabsTrigger>
                                </TabsList>

                                {/* Profile Tab */}
                                <TabsContent value="info" className="space-y-4 mt-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                <Mail className="w-3 h-3" /> E-Mail
                                            </p>
                                            <p className="text-sm font-medium">{selectedMember.email || '-'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                <Phone className="w-3 h-3" /> Telefon
                                            </p>
                                            <p className="text-sm font-medium">{selectedMember.phone || '-'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                <MapPin className="w-3 h-3" /> Standort
                                            </p>
                                            <p className="text-sm font-medium">{selectedMember.location || selectedMember.weather_location || '-'}</p>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                <Clock className="w-3 h-3" /> Wochenstunden
                                            </p>
                                            <p className="text-sm font-medium">{selectedMember.weekly_hours || 40}h</p>
                                        </div>
                                    </div>

                                    {selectedMember.skills && selectedMember.skills.length > 0 && (
                                        <div className="space-y-2">
                                            <p className="text-xs text-muted-foreground">Skills</p>
                                            <div className="flex flex-wrap gap-2">
                                                {selectedMember.skills.map((skill, i) => (
                                                    <Badge key={i} variant="secondary">{skill}</Badge>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {selectedMember.bio && (
                                        <div className="space-y-2">
                                            <p className="text-xs text-muted-foreground">Über mich</p>
                                            <p className="text-sm">{selectedMember.bio}</p>
                                        </div>
                                    )}

                                    {/* Quick Actions */}
                                    <div className="flex gap-2 pt-4 border-t">
                                        <Button onClick={() => handleStartChat(selectedMember.id)} className="flex-1 gap-2">
                                            <MessageSquare className="w-4 h-4" />
                                            Chat
                                        </Button>
                                        <Button onClick={() => handleStartCall(selectedMember.id)} variant="outline" className="flex-1 gap-2">
                                            <Phone className="w-4 h-4" />
                                            Anrufen
                                        </Button>
                                        <Button onClick={() => handleAssignTask(selectedMember.id)} variant="outline" className="flex-1 gap-2">
                                            <CheckSquare className="w-4 h-4" />
                                            Task
                                        </Button>
                                    </div>
                                </TabsContent>

                                {/* Tasks Tab */}
                                <TabsContent value="tasks" className="space-y-4 mt-4">
                                    {memberStats && (
                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                            <Card>
                                                <CardContent className="p-4 text-center">
                                                    <p className="text-3xl font-bold text-orange-500">{memberStats.openTasks}</p>
                                                    <p className="text-xs text-muted-foreground">Offene Tasks</p>
                                                </CardContent>
                                            </Card>
                                            <Card>
                                                <CardContent className="p-4 text-center">
                                                    <p className="text-3xl font-bold text-green-500">{memberStats.completedTasks}</p>
                                                    <p className="text-xs text-muted-foreground">Erledigt</p>
                                                </CardContent>
                                            </Card>
                                        </div>
                                    )}

                                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                                        {memberTasks.filter(t => t.status !== 'done').slice(0, 10).map(task => (
                                            <div 
                                                key={task.id}
                                                className="p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors cursor-pointer"
                                                onClick={() => navigate(`/tasks/${task.id}`)}
                                            >
                                                <div className="flex items-center justify-between">
                                                    <p className="font-medium text-sm truncate">{task.title}</p>
                                                    <Badge variant="outline" className="text-xs capitalize">
                                                        {task.status}
                                                    </Badge>
                                                </div>
                                                {task.due_date && (
                                                    <p className="text-xs text-muted-foreground mt-1">
                                                        Fällig: {new Date(task.due_date).toLocaleDateString('de-DE')}
                                                    </p>
                                                )}
                                            </div>
                                        ))}

                                        {memberTasks.filter(t => t.status !== 'done').length === 0 && (
                                            <p className="text-center text-muted-foreground py-8">
                                                Keine offenen Tasks
                                            </p>
                                        )}
                                    </div>
                                </TabsContent>

                                {/* Workload Tab */}
                                <TabsContent value="workload" className="space-y-4 mt-4">
                                    {memberStats && (
                                        <>
                                            <div className="text-center py-4">
                                                <p className="text-5xl font-bold" style={{ color: getWorkloadColor(memberStats.workloadPercent) }}>
                                                    {memberStats.workloadPercent}%
                                                </p>
                                                <p className="text-muted-foreground mt-1">
                                                    {memberStats.statusLabel || 'Auslastung'}
                                                </p>
                                            </div>

                                            <div className="space-y-2">
                                                <div className="flex justify-between text-sm">
                                                    <span>Geschätzte Stunden</span>
                                                    <span className="font-medium">{memberStats.estimatedHours}h</span>
                                                </div>
                                                <Progress 
                                                    value={Math.min(memberStats.workloadPercent, 100)} 
                                                    className="h-3"
                                                />
                                                <div className="flex justify-between text-xs text-muted-foreground">
                                                    <span>0h</span>
                                                    <span>Kapazität: {memberStats.weeklyCapacity}h</span>
                                                </div>
                                            </div>

                                            {memberStats.overdueTasks > 0 && (
                                                <div className="flex items-center justify-between p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-sm" data-testid="workload-overdue-summary">
                                                    <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
                                                        <Flame className="w-4 h-4" />
                                                        <span className="font-medium">
                                                            {memberStats.overdueTasks} überfällige {memberStats.overdueTasks === 1 ? 'Aufgabe' : 'Aufgaben'} mitgezählt
                                                        </span>
                                                    </div>
                                                    <span className="font-mono text-red-600 dark:text-red-400">
                                                        {memberStats.overdueHours}h
                                                    </span>
                                                </div>
                                            )}

                                            {/* Task Estimates */}
                                            {memberStats.taskEstimates && memberStats.taskEstimates.length > 0 && (
                                                <div className="space-y-2 mt-4">
                                                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                                        Task-Schätzungen
                                                    </p>
                                                    <div className="space-y-1 max-h-[200px] overflow-y-auto">
                                                        {memberStats.taskEstimates.slice(0, 10).map((estimate, idx) => (
                                                            <div key={idx} className={`flex items-center justify-between p-2 rounded text-sm ${estimate.is_overdue ? 'bg-red-500/10 border border-red-500/20' : 'bg-muted/30'}`} data-testid={estimate.is_overdue ? 'workload-task-overdue' : 'workload-task-estimate'}>
                                                                <span className="truncate flex-1 flex items-center gap-2">
                                                                    {estimate.is_overdue && <Flame className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />}
                                                                    <span className="truncate">{estimate.title}</span>
                                                                </span>
                                                                <div className="flex items-center gap-2 ml-2">
                                                                    {estimate.is_overdue && (
                                                                        <Badge variant="outline" className="text-[10px] border-red-500/50 text-red-600 dark:text-red-400">
                                                                            überfällig
                                                                        </Badge>
                                                                    )}
                                                                    {estimate.category && (
                                                                        <Badge variant="outline" className="text-xs">
                                                                            {estimate.category}
                                                                        </Badge>
                                                                    )}
                                                                    <span className="font-medium text-primary">
                                                                        {estimate.estimated_hours}h
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Category Stats */}
                                            {memberStats.categoryStats && Object.keys(memberStats.categoryStats).length > 0 && (
                                                <div className="space-y-2 mt-4">
                                                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                                        Durchschnitt pro Kategorie
                                                    </p>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        {Object.entries(memberStats.categoryStats).map(([cat, stats]) => (
                                                            <div key={cat} className="p-2 rounded bg-muted/30 text-sm">
                                                                <p className="font-medium capitalize">{cat}</p>
                                                                <p className="text-xs text-muted-foreground">
                                                                    Ø {stats.average_hours}h ({stats.task_count} Tasks)
                                                                </p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="p-4 rounded-lg bg-muted/50 mt-4">
                                                {memberStats.recommendation ? (
                                                    <div className="flex items-start gap-2" style={{ color: getWorkloadColor(memberStats.workloadPercent) }}>
                                                        {memberStats.workloadPercent >= 100 ? (
                                                            <Flame className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                                        ) : memberStats.workloadPercent >= 80 ? (
                                                            <Target className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                                        ) : (
                                                            <TrendingUp className="w-5 h-5 flex-shrink-0 mt-0.5" />
                                                        )}
                                                        <span className="font-medium">{memberStats.recommendation}</span>
                                                    </div>
                                                ) : memberStats.workloadPercent >= 100 ? (
                                                    <div className="flex items-center gap-2 text-red-500">
                                                        <Flame className="w-5 h-5" />
                                                        <span className="font-medium">Überlastet - Tasks umverteilen empfohlen</span>
                                                    </div>
                                                ) : memberStats.workloadPercent >= 80 ? (
                                                    <div className="flex items-center gap-2 text-yellow-500">
                                                        <Target className="w-5 h-5" />
                                                        <span className="font-medium">Hohe Auslastung</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2 text-green-500">
                                                        <TrendingUp className="w-5 h-5" />
                                                        <span className="font-medium">Kapazität verfügbar</span>
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </TabsContent>
                            </Tabs>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default TeamPage;
